import * as Sentry from '@sentry/react';
import { ContentType, HeaderKeys } from '@qred/shared-enums';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { ThunkDispatch } from 'redux-thunk';
import { getErrorMessage, getServerError, IBaseError } from '~/services/ErrorMessageService';
import { getUrls, isAxiosError, normalizeIdentifier } from '~/utils';
import { submitCard } from './cardActions';
import { displayErrorMessage, setMissingError, setServerError } from './errorActions';
import { submitLoan } from './loanActions';
import { IAvailableSubmitTypes, IPage } from '~/sharedInterfaces/SharedInterfaces';
import { setLoading, handleWidgetFinished, validatePage } from './widgetContextActions';
import { CLEAR_ERROR, SERVER_ERROR, TERMS_AGREEMENT } from './actionTypes';
import { IDocumentUploadState, IState } from '~/sharedInterfaces/IState';
import { Market } from '~/sharedInterfaces/SharedEnums';

const { UPLOAD_URL } = getUrls();

interface IAwsFields {
  key: string;
  bucket: string;
  Policy: string;
  ['X-Amz-Date']: string;
  ['X-Amz-Algorithm']: string;
  ['X-Amz-Signature']: string;
  ['X-Amz-Credential']: string;
  ['X-Amz-Security-Token']: string;
}
interface IFileInformation {
  url: string;
  fields: IAwsFields;
}

export const contentTypeJSON = {
  headers: { [HeaderKeys.ContentTypeKey]: ContentType.ApplicationJson },
};

const createFilesFromObjectUrls = (documents: IDocumentUploadState[]) => {
  return Promise.all(
    documents.map((document) => {
      return fetch(document.objectURL)
        .then((res) => res.blob())
        .then((blob) => new File([blob], document.name, blob));
    })
  );
};

const getSignedUrl = (
  urlList: string[],
  userIdentifier: string,
  widgetType: keyof IAvailableSubmitTypes
): Promise<AxiosResponse<IFileInformation[]>> => {
  const getSignedUrlsPayload = {
    fileNames: urlList,
    widgetType,
  };
  return axios.post(
    `${UPLOAD_URL}/${normalizeIdentifier(userIdentifier)}`,
    JSON.stringify(getSignedUrlsPayload),
    contentTypeJSON
  );
};

const postFilesToS3 = (
  files: File[],
  userIdentifier: string,
  urlList: IDocumentUploadState[],
  widgetType: keyof IAvailableSubmitTypes
) => {
  const fileKeys = urlList.map((documentUpload) => documentUpload.name);

  return getSignedUrl(fileKeys, userIdentifier, widgetType)
    .then(({ data }) => {
      const fileS3Paths = data.map((file) => file.fields.key);
      return Promise.all(
        data.map((infoContainer: any, i: number) => {
          const form = new FormData();
          Object.keys(infoContainer.fields).forEach((key) =>
            form.append(key, infoContainer.fields[key])
          );
          form.append('file', files[i]);
          return axios.post(infoContainer.url, form).catch((error: AxiosError) => {
            // Silently fail, because we don't care about files failing to upload
            Sentry.withScope((scope) => {
              scope.setFingerprint(['getSignedUrlPost', String(error.message)]);
              Sentry.captureException(error);
            });
          });
        })
      ).then(() => fileS3Paths);
    })
    .catch((error) => {
      Sentry.withScope((scope) => {
        scope.setFingerprint(['getSignedUrl', String(error.message)]);
        Sentry.captureException(error);
      });
      return [''];
    });
};

export const uploadFiles = (
  urlList: IDocumentUploadState[],
  userIdentifier: string,
  widgetType: keyof IAvailableSubmitTypes
) => {
  return createFilesFromObjectUrls(urlList).then((files) =>
    postFilesToS3(files, userIdentifier, urlList, widgetType)
  );
};

export const handleSuccess = () => {
  return (dispatch: ThunkDispatch<any, any, any>) => {
    dispatch(setLoading(false));
    dispatch({ type: CLEAR_ERROR, payload: { errorKey: SERVER_ERROR } });
    dispatch(handleWidgetFinished());
  };
};

export const handleError = (error: AxiosError) => {
  return (dispatch: ThunkDispatch<any, any, any>, getState: () => IState) => {
    const { widgetContext } = getState();
    let hasErrors = false;
    dispatch(setLoading(false));
    if (isAxiosError(error)) {
      const { personalNumber } = JSON.parse(error.config.data);
      dispatch(displayErrorMessage(personalNumber));
      widgetContext.pages.forEach((page: IPage) => {
        if (!dispatch(validatePage(page))) {
          hasErrors = true;
        }
      });
      if (!hasErrors) {
        Sentry.withScope((scope) => {
          scope.setFingerprint(['serverError', String(error.message)]);
          Sentry.captureException(error);
        });
        dispatch(setServerError(getServerError(widgetContext.locale)));
      }
    }
  };
};

// eslint-disable-next-line import/prefer-default-export
export const submitApplication = (widgetType: keyof IAvailableSubmitTypes) => {
  return (dispatch: ThunkDispatch<any, any, any>, getState: () => IState) => {
    const { signup, widgetContext, error } = getState();

    const isMarketNL = widgetContext.settings.market === Market.Netherlands;

    const homeAddressRequestIsPending = signup.homeAddress?.homeAddressRequestStatus === 'pending';
    /*
      TODO: Refactor the possibility to take action based on error/missing state
      
      We cannot use the `missing` properties in the `error` slice of our Redux Store
      to determine if the form data is valid or not. 
      This is because the `missing` property is initialized with an empty string,
      and it's only getting populated when the validation is triggered,
      and the validation is triggered by clicking the [Next] button.
      
      So:
      * In order for me to check is something has an empty value,
      then I need to check the `signup` slice.
      * In order for me to check if something has an invalid value,
      then I need to check the `error` slice.
      
      This is confusing (IMO),
      and I think we should re-work this, and make that more developer friendly.
    */

    const hasInvalidNLFormState = () => {
      if (signup.homeAddress) {
        return (
          signup.homeAddress.houseNumber === '' ||
          signup.homeAddress.postCode === '' ||
          (widgetContext.isAdditionalHouseNumberRequired &&
            signup.homeAddress.houseNumberAddition === '') ||
          (widgetContext.isAdditionalHouseNumberRequired &&
            error.houseNumberAddition?.invalid !== '') ||
          error.postCode?.invalid !== '' ||
          error.homeAddress?.invalid !== '' ||
          !signup.homeAddress?.homeAddressRequestStatus
        );
      }

      return true;
    };

    const availableSubmitTypes = {
      loan: () => dispatch(submitLoan()),
      card: () => dispatch(submitCard()),
    };

    dispatch(validatePage(widgetContext.pages[widgetContext.currentPage]));

    if (!signup.termsAgreement) {
      const { missing } = getErrorMessage(widgetContext.locale, TERMS_AGREEMENT) as IBaseError;
      dispatch(setMissingError(TERMS_AGREEMENT, missing));
      // eslint-disable-next-line no-useless-return
    } else if (!widgetContext.isCompanyValid) return;
    else if (isMarketNL && (hasInvalidNLFormState() || homeAddressRequestIsPending)) {
      // Do nothing
    } else {
      availableSubmitTypes[widgetType]();
    }
  };
};
